import {
  DemandAdjustment,
  DemandAdjustmentTargets,
} from "../../types/demand-factor.type";
import { UNNAMED_ROW, formatMultiplier, joinLabels, labelsOf } from "./constants";

/** The row being drafted in the form, as far as the overlap check needs it. */
export interface OverlapDraft extends DemandAdjustmentTargets {
  /** The row being edited, so it is never compared with itself; null while creating. */
  id: number | null;
  /** Null while the multiplier field does not hold a valid number yet. */
  multiplier: number | null;
}

export interface OverlapNames {
  groupNameByCode: Map<string, string>;
  channelNameByCode: Map<string, string>;
}

export interface OverlapWarning {
  id: number;
  message: string;
}

/**
 * The codes at one level a store can hold and still be reached by both rows. An empty list
 * reaches the whole level, so it defers to the other side; empty on both means the level
 * narrows nothing.
 */
const commonCodes = (left: string[], right: string[]): string[] => {
  if (left.length === 0) return right;
  if (right.length === 0) return left;
  return left.filter((code) => right.includes(code));
};

/** Either side reaches the whole level, or the two lists share a code. */
const levelsMeet = (left: string[], right: string[]): boolean =>
  left.length === 0 || right.length === 0 || left.some((code) => right.includes(code));

/** Channels a store can hold, reached by both rows' targets and excluded by neither. */
const commonChannels = (
  left: DemandAdjustmentTargets,
  right: DemandAdjustmentTargets,
): string[] => {
  const excluded = [...left.exclude_channel, ...right.exclude_channel];
  return commonCodes(left.target_channel, right.target_channel).filter(
    (code) => !excluded.includes(code),
  );
};

/**
 * A store sits under both rows when, on every level, either side is empty or the two lists
 * intersect — and the channels both rows reach are not all excluded by one of them. With no
 * channel target on either side a store always qualifies: one without a channel is never in
 * an exclude list.
 */
export const canShareStore = (
  left: DemandAdjustmentTargets,
  right: DemandAdjustmentTargets,
): boolean => {
  const channelsMeet =
    (left.target_channel.length === 0 && right.target_channel.length === 0) ||
    commonChannels(left, right).length > 0;
  return (
    channelsMeet &&
    levelsMeet(left.target_branch, right.target_branch) &&
    levelsMeet(left.target_route, right.target_route) &&
    levelsMeet(left.target_customer_group, right.target_customer_group) &&
    levelsMeet(left.target_product_code, right.target_product_code)
  );
};

/** The narrowest level both rows pin down, phrased as the stores that get both. */
const describeCommonStores = (
  left: DemandAdjustmentTargets,
  right: DemandAdjustmentTargets,
  names: OverlapNames,
): string => {
  const routes = commonCodes(left.target_route, right.target_route);
  if (routes.length > 0) return `ร้านสาย ${joinLabels(routes)}`;

  const groups = commonCodes(left.target_customer_group, right.target_customer_group);
  if (groups.length > 0) {
    return `ร้านกลุ่ม ${joinLabels(labelsOf(groups, names.groupNameByCode))}`;
  }

  const channels = commonChannels(left, right);
  if (channels.length > 0) {
    return `ร้านช่องทาง ${joinLabels(labelsOf(channels, names.channelNameByCode))}`;
  }

  const branches = commonCodes(left.target_branch, right.target_branch);
  if (branches.length > 0) return `ร้านสาขา ${joinLabels(branches)}`;

  return "ทุกร้าน";
};

const describeOverlap = (
  draft: OverlapDraft,
  row: DemandAdjustment,
  names: OverlapNames,
): string => {
  const overlapsWith = `ซ้อนกับ "${row.name ?? UNNAMED_ROW}"`;
  if (row.multiplier === null) return overlapsWith;

  const withMultiplier = `${overlapsWith} ${formatMultiplier(row.multiplier)}`;
  if (draft.multiplier === null) return withMultiplier;

  // Rows that reach the same store multiply (R1), so the store gets the product of both.
  const combined = formatMultiplier(draft.multiplier * row.multiplier);
  return `${withMultiplier} — ${describeCommonStores(draft, row, names)} จะได้ ${combined}`;
};

/**
 * One warning per saved row that can reach a store the draft also reaches. The caller has
 * already narrowed `rows` to the draft's type and date range.
 */
export const findOverlapWarnings = (
  draft: OverlapDraft,
  rows: DemandAdjustment[],
  names: OverlapNames,
): OverlapWarning[] =>
  rows
    .filter((row) => row.id !== draft.id && canShareStore(draft, row))
    .map((row) => ({ id: row.id, message: describeOverlap(draft, row, names) }));
